import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import { Button } from '../components/ui/button';
import { 
  Activity, 
  Users, 
  UserX, 
  UserCheck, 
  Clock, 
  Shield, 
  Search,
  RefreshCw,
  Calendar,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Eye,
  Filter,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserActivitySummaryPage = () => {
  const { user, token } = useAuth();
  
  // Summary stats
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  
  // User list
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('last_login');
  const [sortOrder, setSortOrder] = useState('desc');
  const [periodDays, setPeriodDays] = useState(30);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  
  // Expanded user details
  const [expandedUser, setExpandedUser] = useState(null);
  const [userHistory, setUserHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const allowedRoles = ['admin', 'super_admin', 'ops_admin'];

  // Fetch summary stats with defensive error handling
  const fetchSummary = useCallback(async () => {
    // Re-read token fresh (no stale cache)
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setSummaryError('Authentication required');
      setSummaryLoading(false);
      return;
    }

    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/activity/summary?days=${periodDays}`,
        { headers: { 'Authorization': `Bearer ${currentToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      } else {
        console.error('Summary fetch failed:', response.status);
        setSummaryError(`Failed to load summary (${response.status})`);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
      setSummaryError(`Network error: ${err.message}`);
    } finally {
      setSummaryLoading(false);
    }
  }, [periodDays]);

  // Fetch user list with defensive error handling
  const fetchUsers = useCallback(async () => {
    // Re-read token fresh (no stale cache)
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setUsersError('Authentication required');
      setUsersLoading(false);
      return;
    }

    setUsersLoading(true);
    setUsersError(null);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (statusFilter !== 'all') {
        params.append('status_filter', statusFilter);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await fetch(
        `${API_URL}/api/admin/activity/users?${params}`,
        { headers: { 'Authorization': `Bearer ${currentToken}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setTotalUsers(data.total || 0);
      } else {
        console.error('Users fetch failed:', response.status);
        setUsersError(`Failed to load users (${response.status})`);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsersError(`Network error: ${err.message}`);
    } finally {
      setUsersLoading(false);
    }
  }, [page, limit, sortBy, sortOrder, statusFilter, searchTerm]);

  // Fetch user history
  const fetchUserHistory = async (userId) => {
    // Re-read token fresh
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return;

    setHistoryLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/activity/users/${userId}/history`,
        { headers: { 'Authorization': `Bearer ${currentToken}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUserHistory(data);
      }
    } catch (err) {
      console.error('Failed to fetch user history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    // Check for token existence, not context token (prevents stale cache)
    if (localStorage.getItem('token')) {
      fetchSummary();
    }
  }, [fetchSummary]);

  useEffect(() => {
    // Check for token existence, not context token (prevents stale cache)
    if (localStorage.getItem('token')) {
      fetchUsers();
    }
  }, [fetchUsers]);

  // Handle user row expansion
  const handleUserClick = (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setUserHistory(null);
    } else {
      setExpandedUser(userId);
      fetchUserHistory(userId);
    }
  };

  // Format date helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  if (!user || !allowedRoles.includes(user.role)) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb 
          items={[
            { label: 'User Activity Summary' }
          ]}
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="page-title">
              <Activity className="h-7 w-7 text-blue-600" />
              User Activity Summary
            </h1>
            <p className="text-gray-600 mt-1">
              Compliance auditing and user activity tracking
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="period-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
            <Button
              variant="outline"
              onClick={() => { fetchSummary(); fetchUsers(); }}
              className="flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {summaryLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white p-4 rounded-lg shadow-sm animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        ) : summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <Users className="h-4 w-4" />
                Total Users
              </div>
              <p className="text-2xl font-bold text-blue-600 mt-1">{summary.total_users}</p>
              <p className="text-xs text-gray-500 mt-1">
                {summary.active_users} active / {summary.inactive_users} inactive
              </p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500">
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <Clock className="h-4 w-4" />
                Logged In ({periodDays}d)
              </div>
              <p className="text-2xl font-bold text-green-600 mt-1">{summary.users_logged_in_period}</p>
              <p className="text-xs text-gray-500 mt-1">
                {summary.new_users_period} new users
              </p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-red-500">
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <UserX className="h-4 w-4" />
                Deactivations ({periodDays}d)
              </div>
              <p className="text-2xl font-bold text-red-600 mt-1">{summary.deactivations_period}</p>
              <p className="text-xs text-gray-500 mt-1">
                {summary.reactivations_period} reactivations
              </p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-orange-500">
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <Shield className="h-4 w-4" />
                Password Resets ({periodDays}d)
              </div>
              <p className="text-2xl font-bold text-orange-600 mt-1">{summary.password_resets_period}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="search-input"
              />
            </div>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              className="px-4 py-2 border border-gray-300 rounded-lg"
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            
            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg"
              data-testid="sort-by"
            >
              <option value="last_login">Last Login</option>
              <option value="created_at">Created Date</option>
              <option value="name">Name</option>
            </select>
            
            {/* Sort Order */}
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              data-testid="sort-order-btn"
            >
              {sortOrder === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              {sortOrder === 'desc' ? 'Newest' : 'Oldest'}
            </button>
          </div>
        </div>

        {/* User Activity Table */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {usersLoading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MFA</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deactivations</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((u) => (
                      <React.Fragment key={u.id}>
                        <tr 
                          className={`hover:bg-gray-50 cursor-pointer ${!u.is_active ? 'bg-gray-50 opacity-75' : ''}`}
                          onClick={() => handleUserClick(u.id)}
                          data-testid={`user-row-${u.id}`}
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${u.is_active ? 'bg-blue-100' : 'bg-gray-200'}`}>
                                <span className={`font-medium ${u.is_active ? 'text-blue-600' : 'text-gray-500'}`}>
                                  {(u.first_name?.[0] || u.email?.[0] || '?').toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.email}
                                </div>
                                <div className="text-sm text-gray-500">{u.email}</div>
                                <div className="text-xs text-gray-400">{u.role?.replace(/_/g, ' ')}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {u.is_active ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <UserCheck className="h-3 w-3" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                <UserX className="h-3 w-3" />
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm">
                              {u.last_login ? (
                                <>
                                  <div className="text-gray-900">{formatRelativeTime(u.last_login)}</div>
                                  {u.days_since_login !== null && u.days_since_login > 30 && (
                                    <div className="flex items-center gap-1 text-amber-600 text-xs mt-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      {u.days_since_login} days ago
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span className="text-gray-400">Never</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {u.mfa_enabled ? (
                              <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                <Shield className="h-4 w-4" />
                                Enabled
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">Disabled</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {u.deactivation_count > 0 ? (
                              <div>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  {u.deactivation_count}x
                                </span>
                                {u.last_deactivation && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Last: {formatRelativeTime(u.last_deactivation.timestamp)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">None</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleUserClick(u.id); }}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              data-testid={`view-history-btn-${u.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                        
                        {/* Expanded History Row */}
                        {expandedUser === u.id && (
                          <tr>
                            <td colSpan={6} className="px-4 py-4 bg-blue-50">
                              {historyLoading ? (
                                <div className="flex justify-center py-4">
                                  <RefreshCw className="h-6 w-6 text-blue-500 animate-spin" />
                                </div>
                              ) : userHistory ? (
                                <div className="space-y-4">
                                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                                    <Activity className="h-4 w-4 text-blue-600" />
                                    Activity History for {userHistory.user.email}
                                  </h4>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Status Events */}
                                    <div className="bg-white rounded-lg p-3 shadow-sm">
                                      <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                        <UserX className="h-4 w-4 text-red-500" />
                                        Status Changes ({userHistory.activity.status_events.length})
                                      </h5>
                                      {userHistory.activity.status_events.length > 0 ? (
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                          {userHistory.activity.status_events.slice(0, 5).map((event, idx) => (
                                            <div key={idx} className="text-xs border-l-2 border-red-300 pl-2">
                                              <div className="font-medium">
                                                {event.action === 'user_deactivated' ? 'Deactivated' : 'Reactivated'}
                                              </div>
                                              <div className="text-gray-500">{formatDate(event.timestamp)}</div>
                                              {event.details?.reason && (
                                                <div className="text-gray-600 italic">&quot;{event.details.reason}&quot;</div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-xs text-gray-400">No status changes</p>
                                      )}
                                    </div>
                                    
                                    {/* Security Events */}
                                    <div className="bg-white rounded-lg p-3 shadow-sm">
                                      <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                        <Shield className="h-4 w-4 text-orange-500" />
                                        Security Events ({userHistory.activity.security_events.length})
                                      </h5>
                                      {userHistory.activity.security_events.length > 0 ? (
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                          {userHistory.activity.security_events.slice(0, 5).map((event, idx) => (
                                            <div key={idx} className="text-xs border-l-2 border-orange-300 pl-2">
                                              <div className="font-medium">{event.action.replace(/_/g, ' ')}</div>
                                              <div className="text-gray-500">{formatDate(event.timestamp)}</div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-xs text-gray-400">No security events</p>
                                      )}
                                    </div>
                                    
                                    {/* Login Events */}
                                    <div className="bg-white rounded-lg p-3 shadow-sm">
                                      <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                        <Clock className="h-4 w-4 text-green-500" />
                                        Login Events ({userHistory.activity.login_events.length})
                                      </h5>
                                      {userHistory.activity.login_events.length > 0 ? (
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                          {userHistory.activity.login_events.slice(0, 5).map((event, idx) => (
                                            <div key={idx} className="text-xs border-l-2 border-green-300 pl-2">
                                              <div className="font-medium">{event.action.replace(/_/g, ' ')}</div>
                                              <div className="text-gray-500">{formatDate(event.timestamp)}</div>
                                              {event.ip_address && (
                                                <div className="text-gray-400">IP: {event.ip_address}</div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-xs text-gray-400">No login events recorded</p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <p className="text-xs text-gray-500">
                                    Total events: {userHistory.total_events} | Created: {formatDate(userHistory.user.created_at)}
                                  </p>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                          <p className="font-medium">No users found</p>
                          <p className="text-sm mt-1">Try adjusting your filters</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalUsers > limit && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-gray-500">
                    Showing {page * limit + 1} - {Math.min((page + 1) * limit, totalUsers)} of {totalUsers}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={(page + 1) * limit >= totalUsers}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UserActivitySummaryPage;
